import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Sparkles, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import api, { formatApiErrorDetail } from '../utils/api';

const TIERS = [25, 50, 75, 100];

const GiftCards = () => {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState('');
  const [form, setForm] = useState({ buyer_name: '', buyer_email: '', recipient_name: '', recipient_email: '', message: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/settings/public').then(({ data }) => setEnabled(!!data.gift_vouchers_enabled)).catch(() => setEnabled(false));
  }, []);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(form.buyer_email) || !emailRegex.test(form.recipient_email)) {
      setError('Please enter valid email addresses.');
      return;
    }
    if (!finalAmount || finalAmount < 10 || finalAmount > 1000) {
      setError('Please choose an amount between €10 and €1000.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/vouchers/purchase', { ...form, amount: finalAmount });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not start payment.');
      setBusy(false);
    }
  };

  if (enabled === false) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-[#faf6ec] text-center px-6" data-testid="giftcards-disabled">
        <Gift className="w-16 h-16 text-[#c5a059]/40 mx-auto mb-4" />
        <h1 className="text-3xl font-serif font-bold text-[#0d3b25] mb-2">Gift Cards</h1>
        <p className="text-[#0d3b25]/60 mb-6">Gift cards are not available online right now. Please contact the salon.</p>
        <Button onClick={() => navigate('/contact')} className="bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold">Contact Us</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf6ec]">
      <section className="pt-32 pb-12 bg-[#04140e] text-[#f5e9ce] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <div className="inline-flex items-center space-x-3 mb-4">
            <span className="h-px w-12 bg-[#c5a059]"></span>
            <span className="text-[#c5a059] text-xs tracking-[0.3em]">— THE PERFECT GIFT</span>
            <span className="h-px w-12 bg-[#c5a059]"></span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold mb-3">Gift Cards</h1>
          <p className="text-[#f5e9ce]/70">Give the gift of beauty & relaxation at 4AN Hair & Beauty Studio.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-2xl mx-auto px-6">
          <form onSubmit={submit} className="bg-white border border-[#c5a059]/30 rounded-sm p-8 shadow-sm space-y-6" data-testid="giftcard-form">
            <div>
              <label className="text-sm font-semibold text-[#0d3b25] mb-3 block">Choose an amount</label>
              <div className="grid grid-cols-4 gap-3">
                {TIERS.map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => { setAmount(v); setCustom(''); }}
                    data-testid={`giftcard-tier-${v}`}
                    className={`py-3 rounded-sm border font-serif font-bold text-lg transition-colors ${
                      !custom && amount === v ? 'bg-[#c5a059] text-[#04140e] border-[#c5a059]' : 'bg-white text-[#0d3b25] border-[#c5a059]/40 hover:bg-[#c5a059]/10'
                    }`}
                  >
                    €{v}
                  </button>
                ))}
              </div>
              <div className="mt-3">
                <label className="text-xs uppercase tracking-wider text-[#0d3b25]/50 block mb-1">Or a custom amount (€)</label>
                <Input
                  type="number" min="10" max="1000" step="5"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="e.g. 120"
                  className="bg-white text-[#0d3b25] border-[#c5a059]/40"
                  data-testid="giftcard-custom-amount"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#0d3b25] mb-2 block">Your name</label>
                <Input required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40" data-testid="giftcard-buyer-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d3b25] mb-2 block">Your email</label>
                <Input type="email" required value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40" data-testid="giftcard-buyer-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d3b25] mb-2 block">Recipient's name</label>
                <Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40" data-testid="giftcard-recipient-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d3b25] mb-2 block">Recipient's email</label>
                <Input type="email" required value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} className="bg-white text-[#0d3b25] border-[#c5a059]/40" data-testid="giftcard-recipient-email" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0d3b25] mb-2 block">Personal message (optional)</label>
              <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Happy Birthday! Enjoy a pamper day on me 💛" className="bg-white text-[#0d3b25] border-[#c5a059]/40" data-testid="giftcard-message" />
            </div>

            <div className="bg-[#faf6ec] border border-[#c5a059]/20 rounded-sm p-4 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#c5a059]" />
              <p className="text-xs text-[#0d3b25]/70">The gift card code is emailed to your recipient right after payment. Valid for 24 months, redeemable online or in the salon.</p>
            </div>

            {error && <div className="bg-red-50 border border-red-300 text-red-700 rounded-sm p-3 text-sm" data-testid="giftcard-error">{error}</div>}

            <Button type="submit" disabled={busy} className="w-full bg-[#c5a059] text-[#04140e] hover:bg-[#d4b56e] rounded-full font-semibold py-6" data-testid="giftcard-submit">
              <Heart className="w-5 h-5 mr-2" /> {busy ? 'Redirecting to payment…' : `Buy Gift Card — €${(finalAmount || 0).toFixed(0)}`}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default GiftCards;
